import { ArrowRight, ChevronDown, Github, Server, Bot, Rocket } from "lucide-react";
import { Button } from "../ui/button";
import { ExternalLink, MarketingLink } from "@/components/OptionalLink";
import useSWR from "swr";
import { JSONSafe, User } from "@/types";
import { fetcherIgnore404, cn } from "@/lib/utils";
import HeroIcons from "@/components/landing/HeroIcons";
import { ShineBorder } from "@/components/magicui/shine-border";
import { RefObject, useId, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

type HeroCards = {
  icon: React.ReactNode;
  title: string;
  summary: string;
  description: string;
  docsHref: string;
};

const HeroValueCard = ({
  icon,
  title,
  summary,
  description,
  docsHref,
  shine,
  isExpanded,
  onToggle
}: {
  icon: React.ReactNode;
  title: string;
  summary: string;
  description: string;
  docsHref: string;
  shine: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const headerId = useId();
  const panelId = useId();

  return (
    <div className="relative z-20 max-w-[550px] border rounded-md bg-muted/60 overflow-hidden">
      {shine && <ShineBorder duration={10} shineColor={["#A07CFE", "#d53f73", "#FFBE7B"]} />}
      <div className="relative p-4">
        <button
          type="button"
          id={headerId}
          className="flex w-full items-start gap-4 text-left cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-expanded={isExpanded}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <span className="shrink-0 pointer-events-none" aria-hidden="true">
            {icon}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-start justify-between gap-2">
              <span className="font-semibold text-xl select-none">{title}</span>
              <ChevronDown
                className={cn(
                  "h-5 w-5 shrink-0 text-muted-foreground transition-transform mt-1",
                  isExpanded && "rotate-180"
                )}
                aria-hidden="true"
              />
            </span>
            <span className="block text-muted-foreground mt-1 text-base select-none">
              {summary}
            </span>
          </span>
        </button>
        {isExpanded && (
          <div
            id={panelId}
            role="region"
            aria-labelledby={headerId}
            className="mt-3 pl-12 pr-0 pb-0 pt-0 border-t border-border/40"
          >
            <p className="text-muted-foreground text-base mt-3 select-none">{description}</p>
            <p className="mt-3">
              <ExternalLink href={docsHref} className="link font-medium text-foreground">
                Learn more in the docs
              </ExternalLink>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const cards: HeroCards[] = [
  {
    icon: <Github className="w-8 h-8" />,
    title: "GitHub to Production",
    summary: "Push code. AI configures everything. Your app goes live.",
    description:
      "Connect to GitHub and add your existing app to DollarDeploy. We will detect your stack, run builds, and ship updates on every push - without hand-writing pipelines or fiddling with deploy scripts.",
    docsHref: "https://docs.dollardeploy.com/blog/self-host-next-js-apps/"
  },
  {
    icon: <Server className="w-8 h-8" />,
    title: "Cloud as Utility",
    summary:
      "Create new server in one click, switch to a different provider or scale your server as your demands change.",
    description:
      "Bring an existing VPS or provision a new machine from supported providers. Infrastructure stays in your account - we handle deployments, configuring and securing your server, and app deployment.",
    docsHref: "https://docs.dollardeploy.com/start-with-digital-ocean/"
  },
  {
    icon: <Rocket className="w-8 h-8" />,
    title: "Batteries Included",
    summary: "HTTPS, monitoring, logs, backups - all handled. No separate tools to configure.",
    description:
      "Let's Encrypt certificates renew automatically, 30-day log retention and monitoring are built in, and your app runs as a native system service—no Docker overhead, no YAML files, no cold starts.",
    docsHref: "https://docs.dollardeploy.com/#supported-app-types"
  },
  {
    icon: <Bot className="w-8 h-8" />,
    title: "AI That Acts, Not Advises",
    summary:
      "Tell the AI what you need. It deploys, provisions, and fixes - on your real servers.",
    description:
      "Describe what you want in plain language - redeploy an app, provision a new server, or trace a production error. The agent proposes and automatically runs changes on your servers instead of generic tips.",
    docsHref: "https://docs.dollardeploy.com/ai/"
  }
];

export const Hero = () => {
  const { data: user } = useSWR<JSONSafe<User>>("/api/user", fetcherIgnore404);
  const refContainer = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number>(1);
  const [textClass, setTextClass] = useState<string | undefined>(undefined);

  useResizeObserver<HTMLDivElement>({
    ref: refContainer as RefObject<HTMLDivElement>,
    box: "border-box",
    onResize: function (size) {
      // Only scale on the lg breakpoint and above
      if (size.width && size.width > 900) {
        if (size.height && size.height < 560) {
          const newScale = size.height / 540;
          setScale(newScale);
          if (newScale > 0.8) {
            setTextClass(undefined);
          } else if (newScale > 0.6) {
            setTextClass("text-3xl mb-2");
          } else {
            setTextClass("text-2xl");
          }
        } else {
          setScale(1);
        }
      } else {
        setScale(1);
        setTextClass(undefined);
      }
    }
  });

  return (
    <section
      ref={refContainer}
      id="hero"
      className="relative lg:h-[calc(100vh-60px)] mt-20 lg:mt-0"
    >
      <div className="hidden dark:block pointer-events-none absolute inset-x-0 top-0 h-32 z-10 bg-gradient-to-b from-background to-transparent" />
      <div className="hidden dark:block pointer-events-none absolute inset-x-0 bottom-0 h-32 z-10 bg-gradient-to-t from-background to-transparent" />
      <div className="relative z-20 h-full container gap-20 lg:gap-5 flex flex-col lg:flex-row items-center justify-center lg:justify-between px-4 xl:px-20">
        <div className="flex flex-col gap-2 items-center lg:items-start">
          <h1
            className={cn(
              "font-bold tracking-tighter text-balance text-center lg:text-left",
              textClass ? textClass : "text-4xl md:text-5xl lg:text-6xl mb-4"
            )}
          >
            Your Apps. Your Infra.{" "}
            <span className="bg-gradient-to-r from-[#ff7f50] to-[#ffd700] text-transparent bg-clip-text">
              Zero DevOps.
            </span>
          </h1>

          <p className="text-center mb-4 lg:text-left text-lg md:text-xl text-muted-foreground text-balance">
            Your apps, running in production - working fast for your users, optimized for
            search engines and AI visibility.
          </p>

          <div className="flex justify-center lg:justify-start w-full mb-6">
            <HeroIcons className="w-[340px] p-0" />
          </div>

          <div className="flex flex-col items-center lg:items-start gap-2 w-full">
            <Button size="lg" asChild>
              <MarketingLink
                href={user ? "/dashboard" : "/auth/signUp?callbackUrl=/dashboard"}
                className="w-50 flex items-center"
              >
                <span className="ml-2">Deploy your first app</span>{" "}
                <ArrowRight className="h-5 w-5 mr-2" />
              </MarketingLink>
            </Button>
            {process.env.NEXT_PUBLIC_FREE_TRIAL_DAYS && (
              <p className="text-sm text-muted-foreground text-center lg:text-left text-balance">
                Subscribe now with a {process.env.NEXT_PUBLIC_FREE_TRIAL_DAYS}-day free trial.
              </p>
            )}
          </div>
        </div>

        <HeroCards cards={cards} scale={scale} />
      </div>
    </section>
  );
};

const HeroCards = ({ cards, scale }: { cards: HeroCards[]; scale: number }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleToggleCard = (index: number) => {
    setExpandedIndex(prev => (prev === index ? null : index));
  };

  return (
    <div
      className="relative flex valuePropositions flex-col gap-4 rounded-xl"
      style={scale !== 1 ? { transform: `scale(${scale})` } : undefined}
    >
      {cards.map((card, index) => (
        <HeroValueCard
          key={card.title}
          icon={card.icon}
          title={card.title}
          summary={card.summary}
          description={card.description}
          docsHref={card.docsHref}
          shine={expandedIndex === index}
          isExpanded={expandedIndex === index}
          onToggle={() => handleToggleCard(index)}
        />
      ))}
    </div>
  );
};
